/**
 * Helpers for Planning Engine Input Scope vs scenario-bound container.
 *
 * User-chosen exploration scopes (Item Pool, Collection, …) must not be
 * overwritten by a *derived* scenario_portfolio bind while an async resolve
 * is in flight — that was resetting the picker back to "Scenario priorities".
 */

import type { Scenario } from './planningEngineModule.types';
import type { EngineInputSource } from './planningEngineService';
import { scenarioHasHardScope } from './scenarioHardScope';

const USER_EXPLORATION_TYPES = new Set<EngineInputSource['type']>([
  'item_pool',
  'collection',
  'queue',
  'published_priority_list',
]);

/** Named scopes the user can pick for exploration (not live / not scenario_portfolio). */
export function isUserExplorationScope(source: EngineInputSource): boolean {
  return USER_EXPLORATION_TYPES.has(source.type);
}

/**
 * True when `bound` is the synthetic "Scenario priorities" container derived
 * from planning_priorities rows — not a persisted input_source_* bind.
 */
export function isDerivedScenarioPortfolio(
  bound: EngineInputSource | null,
  scenario: Pick<Scenario, 'inputSourceType' | 'inputSourceId'> | null | undefined,
): boolean {
  return bound?.type === 'scenario_portfolio' && !scenarioHasHardScope(scenario);
}

/**
 * Whether an in-flight bound-scope apply should skip writing because the user
 * already chose a named exploration scope for this session.
 */
export function shouldPreserveUserExplorationScope(
  current: EngineInputSource,
  bound: EngineInputSource | null,
  scenario: Pick<Scenario, 'inputSourceType' | 'inputSourceId'> | null | undefined,
): boolean {
  return isUserExplorationScope(current) && isDerivedScenarioPortfolio(bound, scenario);
}
